import React from 'react';
import { Banknote, Shield, Handshake } from 'lucide-react';
import type { SavCosts } from '../types/dashboard.types';

interface CostComparisonCardsProps {
  costs: SavCosts;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export const CostComparisonCards: React.FC<CostComparisonCardsProps> = ({
  costs,
}) => {
  const warrantyRate = costs.totalAmount > 0
    ? Math.round((costs.warrantyAmount / costs.totalAmount) * 100 * 10) / 10
    : 0;
  const commercialRate = costs.totalAmount > 0
    ? Math.round((costs.commercialAmount / costs.totalAmount) * 100 * 10) / 10
    : 0;

  const cards = [
    {
      icon: Banknote,
      label: 'Total prises en charge',
      total: costs.totalAmount,
      avgPerClaim: costs.avgAmount,
      avgPerContract: costs.avgAmountPerContract,
      rate: null as number | null,
      color: 'tw-bg-red-50 tw-text-red-600',
      valueColor: 'tw-text-red-700',
    },
    {
      icon: Shield,
      label: 'Prises en charge au titre du contrat',
      total: costs.warrantyAmount,
      avgPerClaim: costs.avgWarrantyAmount,
      avgPerContract: costs.avgWarrantyAmountPerContract,
      rate: warrantyRate,
      color: 'tw-bg-orange-50 tw-text-orange-600',
      valueColor: 'tw-text-orange-700',
    },
    {
      icon: Handshake,
      label: 'Prises en charge à titre commercial',
      total: costs.commercialAmount,
      avgPerClaim: costs.avgCommercialAmount,
      avgPerContract: costs.avgCommercialAmountPerContract,
      rate: commercialRate,
      color: 'tw-bg-orange-50 tw-text-orange-600',
      valueColor: 'tw-text-orange-700',
    },
  ];

  return (
    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 sm:tw-p-5"
        >
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
            <div className={`tw-p-2 tw-rounded-lg ${card.color}`}>
              <card.icon className="tw-w-5 tw-h-5" />
            </div>
            <span className="tw-text-sm tw-font-medium tw-text-gray-600">
              {card.label}
            </span>
          </div>
          <p className={`tw-text-lg sm:tw-text-xl md:tw-text-2xl tw-font-bold ${card.valueColor}`}>
            {formatCurrency(card.total)}
            {card.rate !== null && (
              <span className="tw-text-sm tw-font-normal tw-text-gray-500 tw-ml-2">({card.rate}%)</span>
            )}
          </p>
          <p className="tw-text-xs sm:tw-text-sm tw-text-gray-500 tw-mt-1">
            Moy / panne : {formatCurrency(card.avgPerClaim)} <span className="tw-mx-1 tw-text-gray-300">|</span> Moy / contrat : {formatCurrency(card.avgPerContract)}
          </p>
        </div>
      ))}
    </div>
  );
};
